import jsep from "jsep";
import { ArrayExpression, BinaryExpression, CallExpression, ConditionalExpression, Expression, Identifier, Literal, MemberExpression, ThisExpression, UnaryExpression } from "jsep";

export function evaluate(prop: any, context: any): any {
    if (Array.isArray(prop)) {
        return prop.map(e => evaluate(e, context));
    }
    if (prop in [null, undefined]
        || typeof prop !== 'object') {
        return prop;
    }

    if (Object.keys(prop).length !== 1 || Object.keys(prop)[0] !== '[binding]') {
        // recursive prop evaluation
        return Object.entries(prop).reduce((acc, [k, v]) => {
            acc[k] = evaluate(v, context);
            return acc;
        }, {});
    }

    const binding = prop['[binding]'];
    try {
        switch (binding.type) {
            case 'expression':
                const expr = parseExpression(binding.expression);
                const val = evaluateExpression(expr, context);
                return val;
            default:
                throw new Error('Unsupported binding type: ' + binding.type);
        }
    } catch (e) {
        return e;
    }
}

function parseExpression(expression: string): Expression {
    return jsep(expression);
}

function evaluateExpression(expr: Expression, context: any): any {

    switch (expr.type) {
        case 'ArrayExpression':
            return evaluateArrayExpression(expr as ArrayExpression, context);
        case 'BinaryExpression':
            return evaluateBinaryExpression(expr as BinaryExpression, context);
        case 'CallExpression':
            return evaluateCallExpression(expr as CallExpression, context);
        case 'ConditionalExpression':
            return evaluateConditionalExpression(expr as ConditionalExpression, context);
        case 'Identifier':
            return evaluateIdentifier(expr as Identifier, context);
        case 'Literal':
            return evaluateLiteral(expr as Literal, context);
        case 'MemberExpression':
            return evaluateMemberExpression(expr as MemberExpression, context);
        case 'ThisExpression':
            return evaluateThisExpression(expr as ThisExpression, context);
        case 'UnaryExpression':
            return evaluateUnaryExpression(expr as UnaryExpression, context);
        default:
            throw new Error('Unsupported expression type: ' + expr.type);
    }
}

function evaluateArrayExpression(expr: ArrayExpression, context: any): any {
    return expr.elements.map(e => e && evaluateExpression(e, context))
}

function evaluateBinaryExpression(expr: BinaryExpression, context: any): any {
    const ops = {
        '+': (l, r) => l + r,
        '-': (l, r) => l - r,
        '*': (l, r) => l * r,
        '/': (l, r) => l / r,
        '%': (l, r) => l % r,
        '&': (l, r) => l & r,
        '|': (l, r) => l | r,
        '>': (l, r) => l > r,
        '>=': (l, r) => l >= r,
        '<': (l, r) => l < r,
        '<=': (l, r) => l <= r,
        '==': (l, r) => l == r,
        '!=': (l, r) => l != r,
        '&&': (l, r) => (l && r) ? true : false,
        '||': (l, r) => (l || r) ? true : false,
        '^': (l, r) => Math.pow(l, r),
    }
    if (!Object.keys(ops).includes(expr.operator)) {
        throw new Error('Unsupported binary expression operator: ' + expr.operator);
    }
    const left = evaluateExpression(expr.left, context);
    const right = evaluateExpression(expr.right, context);
    return ops[expr.operator](left, right);
}

function evaluateCallExpression(expr: CallExpression, context: any): any {
    const fns = {
        'if': (cond, ifTrue, ifFalse) => cond ? ifTrue : ifFalse,
    }

    const fn = evaluateExpression(expr.callee, context);
    if (!Object.keys(fns).includes(fn)) {
        throw new Error('Unsupported call expression function: ' + fn);
    }
    const args = expr.arguments.map(e => evaluateExpression(e, context));
    const expectedArgCount = (fns[fn] as Function).length;
    if (expectedArgCount != args.length) {
        throw new Error('Argument count mismatch in call expression function: ' + fn + '. Expected ' + expectedArgCount + ' but got ' + args.length);
    }
    return fns[fn](...args);
}

function evaluateConditionalExpression(expr: ConditionalExpression, context: any): any {
    const cond = evaluateExpression(expr.test, context);
    const ifTrue = evaluateExpression(expr.consequent, context);
    const ifFalse = evaluateExpression(expr.alternate, context);
    return cond ? ifTrue : ifFalse;
}

function evaluateIdentifier(expr: Identifier, context: any): string {
    return expr.name;
}

function evaluateLiteral(expr: Literal, context: any): any {
    return expr.value;
}

function evaluateMemberExpression(expr: MemberExpression, context: any): any {
    const obj = evaluateExpression(expr.object, context);
    const prop = evaluateExpression(expr.property, context);
    if (expr.computed) { //array access
        return obj[prop];
    } else { //object prop access
        return obj[prop];
    }
}

function evaluateThisExpression(expr: ThisExpression, context: any): any {
    return context;
}

function evaluateUnaryExpression(expr: UnaryExpression, context: any): any {
    const ops = {
        '+': (o) => +o,
        '-': (o) => -o,
        '!': (o) => !o,
        '~': (o) => ~o,
    }

    if (!expr.prefix) {
        throw new Error('Unsupported postfix unary expression operator: ' + expr.operator);
    }

    if (!Object.keys(ops).includes(expr.operator)) {
        throw new Error('Unsupported unary expression operator: ' + expr.operator);
    }
    const arg = evaluateExpression(expr.argument, context);
    return ops[expr.operator](arg);
}
